import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { InformationBoxComponent } from '@principle-theorem/ng-principle-shared';
import { NgCommonModule, TypedFormControl } from '@principle-theorem/ng-shared';
import {
  type IDepositTreatmentConfiguration,
  type ITreatmentConfiguration,
} from '@principle-theorem/principle-core/interfaces';
import { type WithRef } from '@principle-theorem/shared';
import * as stringify from 'json-stable-stringify';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { startWith } from 'rxjs/operators';
import { DepositAmountConversion } from './deposit-amount-conversion';
import { DepositSectionHeadingComponent } from './deposit-section-heading.component';

export interface IDepositTreatmentConfigurationState {
  readonly dirty: boolean;
  readonly valid: boolean;
}

interface IDepositTreatmentConfigurationRow {
  readonly configuration: WithRef<ITreatmentConfiguration>;
  readonly amountDollars: TypedFormControl<number | undefined>;
}

@Component({
  selector: 'pr-deposit-treatment-configuration-settings',
  templateUrl: './deposit-treatment-configuration-settings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    InformationBoxComponent,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    MatTooltipModule,
    DepositSectionHeadingComponent,
  ],
})
export class DepositTreatmentConfigurationSettingsComponent {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _searchControl = new TypedFormControl('');
  private readonly _search = toSignal(
    this._searchControl.valueChanges.pipe(startWith('')),
    { initialValue: '' }
  );
  private readonly _selectedConfigurationIdsControl = new TypedFormControl<
    string[]
  >([]);
  private readonly _selectedConfigurationIds = signal<string[]>([]);
  private _dirty = false;
  private _inputSignature?: string;
  readonly treatmentConfigurations = input<WithRef<ITreatmentConfiguration>[]>(
    []
  );
  readonly value = input<Record<string, IDepositTreatmentConfiguration>>({});
  readonly stateChange = output<IDepositTreatmentConfigurationState>();
  readonly searchControl = this._searchControl;
  readonly selectedConfigurationIds = this._selectedConfigurationIdsControl;
  readonly rows = signal<IDepositTreatmentConfigurationRow[]>([]);

  readonly filteredConfigurations = computed(() => {
    const search = this._search().trim().toLowerCase();
    const selectedIds = new Set(this._selectedConfigurationIds());
    return this.treatmentConfigurations().filter(
      (configuration) =>
        selectedIds.has(configuration.ref.id) ||
        !search ||
        configuration.name.toLowerCase().includes(search)
    );
  });

  constructor() {
    effect(() => {
      const configurations = this.treatmentConfigurations();
      const value = this.value();
      const inputSignature = stringify({
        configurations: configurations.map((configuration) => ({
          id: configuration.ref.id,
          name: configuration.name,
        })),
        value,
      });
      if (inputSignature === this._inputSignature) {
        return;
      }
      this._inputSignature = inputSignature;
      const authoritativeSelectedIds = Object.keys(value).filter(
        (configurationId) =>
          configurations.some(
            (configuration) => configuration.ref.id === configurationId
          )
      );
      const selectedIds = this.selectedConfigurationIds.dirty
        ? this.selectedConfigurationIds.value
        : authoritativeSelectedIds;
      if (this.selectedConfigurationIds.pristine) {
        this.selectedConfigurationIds.setValue(selectedIds, {
          emitEvent: false,
        });
      }
      this._selectedConfigurationIds.set(selectedIds);
      untracked(() => this._setRows(selectedIds, value, true));
      this._dirty =
        this.selectedConfigurationIds.dirty ||
        this.rows().some((row) => row.amountDollars.dirty);
      this._emitState();
    });

    this.selectedConfigurationIds.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((selectedIds) => {
        this._selectedConfigurationIds.set(selectedIds);
        this._dirty = true;
        this._setRows(selectedIds, this.getRawValue());
        this._emitState();
      });
  }

  get valid(): boolean {
    return this.rows().every((row) => row.amountDollars.valid);
  }

  getRawValue(): Record<string, IDepositTreatmentConfiguration> {
    const knownConfigurationIds = new Set(
      this.treatmentConfigurations().map(
        (configuration) => configuration.ref.id
      )
    );
    const unresolvedDeposits = Object.fromEntries(
      Object.entries(this.value()).filter(
        ([configurationId]) => !knownConfigurationIds.has(configurationId)
      )
    );
    return this.rows().reduce<Record<string, IDepositTreatmentConfiguration>>(
      (deposits, row) => {
        const amountCents = DepositAmountConversion.centsFromDollars(
          row.amountDollars.value
        );
        return amountCents
          ? {
              ...deposits,
              [row.configuration.ref.id]: { amountCents },
            }
          : deposits;
      },
      unresolvedDeposits
    );
  }

  markAsPristine(): void {
    this._dirty = false;
    this.selectedConfigurationIds.markAsPristine();
    for (const row of this.rows()) {
      row.amountDollars.markAsPristine();
    }
    this._emitState();
  }

  removeConfiguration(configurationId: string): void {
    this.selectedConfigurationIds.setValue(
      this.selectedConfigurationIds.value.filter(
        (selectedId) => selectedId !== configurationId
      )
    );
  }

  private _setRows(
    selectedIds: string[],
    deposits: Record<string, IDepositTreatmentConfiguration>,
    reconcilePristineRows = false
  ): void {
    const currentRows = new Map(
      this.rows().map((row) => [row.configuration.ref.id, row])
    );
    const rows = selectedIds
      .map((configurationId) => {
        const configuration = this.treatmentConfigurations().find(
          (candidate) => candidate.ref.id === configurationId
        );
        if (!configuration) {
          return;
        }
        const currentRow = currentRows.get(configurationId);
        if (currentRow) {
          if (reconcilePristineRows && currentRow.amountDollars.pristine) {
            currentRow.amountDollars.setValue(
              DepositAmountConversion.dollarsFromCents(
                deposits[configurationId]?.amountCents
              ),
              { emitEvent: false }
            );
          }
          return {
            configuration,
            amountDollars: currentRow.amountDollars,
          };
        }
        const amountDollars = new TypedFormControl<number | undefined>(
          DepositAmountConversion.dollarsFromCents(
            deposits[configurationId]?.amountCents
          ),
          [Validators.required, Validators.min(0.01)]
        );
        amountDollars.valueChanges
          .pipe(takeUntilDestroyed(this._destroyRef))
          .subscribe(() => {
            this._dirty = true;
            this._emitState();
          });
        return { configuration, amountDollars };
      })
      .filter(
        (row): row is IDepositTreatmentConfigurationRow => row !== undefined
      );
    this.rows.set(rows);
  }

  private _emitState(): void {
    this.stateChange.emit({ dirty: this._dirty, valid: this.valid });
  }
}
